import {
  useEffect,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { passwordProblemMessage, passwordProblems, type PasswordPolicy } from '@wr/shared';

/**
 * Shared components, drawn exactly as docs/prototype.html (CLAUDE.md): every
 * screen builds on them, and no screen carries its own styles.
 */

export function Notice({
  kind = 'info',
  children,
}: {
  kind?: 'info' | 'ok' | 'error' | 'warn';
  children: ReactNode;
}) {
  return (
    <div className={`notice ${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

/** The label of a field, with the red star of a required field (aria-hidden). */
function FieldLabel({ id, text, mark }: { id: string; text: string; mark?: boolean }) {
  return (
    <span>
      <span id={id}>{text}</span>
      {mark && (
        <>
          {' '}
          <span className="req" aria-hidden="true">
            *
          </span>
        </>
      )}
    </span>
  );
}

/**
 * Help of a field, inside its label as in the prototype: the hint above the
 * field, the error (which replaces it) below. Out of the field's name: the
 * name comes from aria-labelledby, the help from aria-describedby.
 */
function Help({ id, error, hint }: { id: string; error?: string; hint?: ReactNode }) {
  if (!error && !hint) return null;
  return (
    <small id={id} className={error ? 'err' : undefined}>
      {error ?? hint}
    </small>
  );
}

interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: string;
  /** Shows the red star of a required field. */
  mark?: boolean;
}

function fieldA11y(id: string, error?: string, hint?: ReactNode) {
  return {
    id,
    'aria-labelledby': `${id}-label`,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error || hint ? `${id}-help` : undefined,
    className: error ? 'invalid' : undefined,
  };
}

/** A text field (prototype: label.f). */
export function Field({
  label,
  hint,
  error,
  mark,
  id: givenId,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & FieldProps) {
  const generated = useId();
  const id = givenId ?? generated;
  return (
    <label className="f">
      <FieldLabel id={`${id}-label`} text={label} mark={mark} />
      {!error && <Help id={`${id}-help`} hint={hint} />}
      <input type="text" {...input} {...fieldA11y(id, error, hint)} />
      {error && <Help id={`${id}-help`} error={error} />}
    </label>
  );
}

export function SelectField({
  label,
  hint,
  error,
  mark,
  id: givenId,
  children,
  ...select
}: SelectHTMLAttributes<HTMLSelectElement> & FieldProps) {
  const generated = useId();
  const id = givenId ?? generated;
  return (
    <label className="f">
      <FieldLabel id={`${id}-label`} text={label} mark={mark} />
      {!error && <Help id={`${id}-help`} hint={hint} />}
      <select {...select} {...fieldA11y(id, error, hint)}>
        {children}
      </select>
      {error && <Help id={`${id}-help`} error={error} />}
    </label>
  );
}

export function TextAreaField({
  label,
  hint,
  error,
  mark,
  id: givenId,
  ...area
}: TextareaHTMLAttributes<HTMLTextAreaElement> & FieldProps) {
  const generated = useId();
  const id = givenId ?? generated;
  return (
    <label className="f">
      <FieldLabel id={`${id}-label`} text={label} mark={mark} />
      {!error && <Help id={`${id}-help`} hint={hint} />}
      <textarea {...area} {...fieldA11y(id, error, hint)} />
      {error && <Help id={`${id}-help`} error={error} />}
    </label>
  );
}

/** A real checkbox with its text beside it, aligned on the first line (brief 6.1). */
export function Checkbox({
  children,
  ...input
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { children: ReactNode }) {
  return (
    <label className="consent-row">
      <input type="checkbox" {...input} />
      <span>{children}</span>
    </label>
  );
}

/** A question answered with chips (prototype: .fm-q, .fm-l and its chips). */
function ChipQuestion({
  id,
  label,
  mark,
  error,
  children,
}: {
  id: string;
  label: string;
  mark?: boolean;
  error?: string;
  children: ReactNode;
}) {
  return (
    <fieldset
      className="fm-q"
      id={id}
      tabIndex={-1}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${id}-help` : undefined}
    >
      <legend className="fm-l">
        {label}
        {mark && (
          <>
            {' '}
            <span className="req" aria-hidden="true">
              *
            </span>
          </>
        )}
      </legend>
      <div className="chips">{children}</div>
      <Help id={`${id}-help`} error={error} />
    </fieldset>
  );
}

/** One answer at most; tapping the chosen one again clears it (prototype: .le-chip). */
export function OneOfChips({
  id: givenId,
  label,
  options,
  value,
  onChange,
  mark,
  error,
}: {
  id?: string;
  label: string;
  options: readonly { value: string; label: string }[];
  value: string;
  onChange(value: string): void;
  mark?: boolean;
  error?: string;
}) {
  const generated = useId();
  return (
    <ChipQuestion id={givenId ?? generated} label={label} mark={mark} error={error}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`le-chip${value === o.value ? ' on' : ''}`}
          aria-pressed={value === o.value}
          onClick={() => onChange(value === o.value ? '' : o.value)}
        >
          {o.label}
        </button>
      ))}
    </ChipQuestion>
  );
}

/** Several answers (prototype: label.chip with a hidden checkbox). */
export function ManyOfChips({
  id: givenId,
  label,
  options,
  value,
  onChange,
  mark,
  error,
}: {
  id?: string;
  label: string;
  options: readonly { value: string; label: string }[];
  value: readonly string[];
  onChange(value: string[]): void;
  mark?: boolean;
  error?: string;
}) {
  const generated = useId();
  return (
    <ChipQuestion id={givenId ?? generated} label={label} mark={mark} error={error}>
      {options.map((o) => (
        <label key={o.value} className="chip">
          <input
            type="checkbox"
            checked={value.includes(o.value)}
            onChange={(e) =>
              onChange(
                options
                  .map((x) => x.value)
                  .filter((v) => (v === o.value ? e.target.checked : value.includes(v))),
              )
            }
          />
          {o.label}
        </label>
      ))}
    </ChipQuestion>
  );
}

/** A card with a title and an optional note (prototype: .ncard). */
export function Card({
  title,
  note,
  children,
  className,
}: {
  title?: ReactNode;
  note?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`ncard${className ? ` ${className}` : ''}`}>
      {(title || note) && (
        <div className="ncard-h">
          {title && <h2 className="sec">{title}</h2>}
          {note && <span className="note">{note}</span>}
        </div>
      )}
      <div className="vstack">{children}</div>
    </section>
  );
}

/** The title of a page, its actions and its lede (prototype: .page-head, h1.page, .lede). */
export function PageHead({
  title,
  lede,
  actions,
}: {
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <>
      <div className="page-head">
        <h1 className="page">{title}</h1>
        {actions && <div className="actions">{actions}</div>}
      </div>
      {lede && <p className="lede">{lede}</p>}
    </>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <strong>{title}</strong>
      {children}
    </div>
  );
}

/**
 * A window (prototype: <dialog> with .dlg-head, .dlg-body, .dlg-foot): full
 * screen on a phone, centered on larger screens. Escape and the close button
 * call onClose; the caller decides (a form with changes asks first).
 */
export function Dialog({
  title,
  onClose,
  footer,
  children,
  small,
  className,
}: {
  title: string;
  onClose(): void;
  footer?: ReactNode;
  children: ReactNode;
  small?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) {
      // jsdom has no showModal: the window is then simply open.
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    return () => dialog?.close?.();
  }, []);
  return (
    <dialog
      ref={ref}
      className={`dlg-modal${small ? ' dlg-small' : ''}${className ? ` ${className}` : ''}`}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div className="dlg">
        <div className="dlg-head">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="x" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="dlg-body">{children}</div>
        {footer && <div className="dlg-foot">{footer}</div>}
      </div>
    </dialog>
  );
}

/**
 * New password and its confirmation, with the rules explained as the person
 * types. The API checks again (and refuses common passwords).
 */
export function NewPasswordFields({
  policy,
  personal,
  value,
  onChange,
  serverProblems,
}: {
  policy: PasswordPolicy;
  personal: string[];
  value: { password: string; confirm: string };
  onChange(value: { password: string; confirm: string }): void;
  serverProblems?: string[];
}) {
  const [touched, setTouched] = useState(false);
  const problems = passwordProblems(value.password, policy, personal);
  const local =
    touched && value.password !== '' ? problems.map((p) => passwordProblemMessage(p, policy)) : [];
  const error = [...local, ...(serverProblems ?? [])].join(' ') || undefined;
  const mismatch = touched && value.confirm !== '' && value.confirm !== value.password;
  return (
    <>
      <Field
        label="New password"
        type="password"
        autoComplete="new-password"
        required
        value={value.password}
        onChange={(e) => onChange({ ...value, password: e.target.value })}
        onBlur={() => setTouched(true)}
        hint={`At least ${policy.minLength} characters. A short sentence is easy to remember.`}
        error={error}
      />
      <Field
        label="Confirm the new password"
        type="password"
        autoComplete="new-password"
        required
        value={value.confirm}
        onChange={(e) => onChange({ ...value, confirm: e.target.value })}
        onBlur={() => setTouched(true)}
        error={mismatch ? 'The two passwords are different.' : undefined}
      />
    </>
  );
}

/** Whether the new password may be sent (the same checks as NewPasswordFields). */
export function newPasswordReady(
  value: { password: string; confirm: string },
  policy: PasswordPolicy,
  personal: string[],
): boolean {
  return (
    value.password !== '' &&
    value.password === value.confirm &&
    passwordProblems(value.password, policy, personal).length === 0
  );
}

/** The one-time recovery codes, shown once, with copy and download. */
export function RecoveryCodes({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState(false);
  const text = `Waste Robotics Sales Portal: recovery codes\nEach code works once.\n\n${codes.join('\n')}\n`;
  const download = `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`;
  return (
    <div className="codes">
      <ol aria-label="Recovery codes">
        {codes.map((code) => (
          <li key={code}>
            <code>{code}</code>
          </li>
        ))}
      </ol>
      <div className="actions">
        <button
          type="button"
          className="btn sm"
          onClick={() => {
            void navigator.clipboard?.writeText(text).then(() => setCopied(true));
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <a className="btn sm" href={download} download="wr-sales-portal-recovery-codes.txt">
          Download
        </a>
      </div>
    </div>
  );
}

/** A button that asks once more before a change that cannot be undone. */
export function ConfirmButton({
  label,
  question,
  onConfirm,
  danger,
  disabled,
}: {
  label: string;
  question: string;
  onConfirm(): void;
  danger?: boolean;
  disabled?: boolean;
}) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <button
        type="button"
        className={`btn${danger ? ' danger' : ''}`}
        disabled={disabled}
        onClick={() => setAsking(true)}
      >
        {label}
      </button>
    );
  }
  return (
    <div className="confirm" role="group" aria-label={label}>
      <span>{question}</span>
      <button
        type="button"
        className="btn primary sm"
        onClick={() => {
          setAsking(false);
          onConfirm();
        }}
      >
        Yes, {label.charAt(0).toLowerCase() + label.slice(1)}
      </button>
      <button type="button" className="btn sm" onClick={() => setAsking(false)}>
        Cancel
      </button>
    </div>
  );
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'Never';
  return new Date(value).toLocaleString('en-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function initials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
}

/** The one-time token of an email link (after #token=). */
export function tokenFromHash(): string | null {
  return /(?:^#|&)token=([A-Za-z0-9_-]+)/.exec(window.location.hash)?.[1] ?? null;
}

/** Removes the token from the address bar and the history, once it is read. */
export function clearHash(): void {
  if (window.location.hash) {
    window.history.replaceState(window.history.state, '', window.location.pathname);
  }
}

/** One answer, as radio chips (prototype: label.chip with a hidden radio, Start the lead, To do). */
export function RadioChips({
  id: givenId,
  label,
  options,
  value,
  onChange,
  mark,
  error,
}: {
  id?: string;
  label: string;
  options: readonly { value: string; label: string }[];
  value: string;
  onChange(value: string): void;
  mark?: boolean;
  error?: string;
}) {
  const generated = useId();
  const id = givenId ?? generated;
  return (
    <ChipQuestion id={id} label={label} mark={mark} error={error}>
      {options.map((o) => (
        <label key={o.value} className="chip">
          <input
            type="radio"
            name={`${id}-radio`}
            value={o.value}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
          />
          {o.label}
        </label>
      ))}
    </ChipQuestion>
  );
}
